import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Select, Table } from 'antd';
import styles from './BindWorkplace.module.css';
import Button from '../../Button/Button';
import Spinner from '../../Spinner/Spinner';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { SurfaceBar } from '../../SurfaceBar/SurfaceBar';
import useApi from '../../../hooks/useApi.hook';
import useFillSelect from '../../../hooks/useFillSelect.hook';
import {
	bindWorkplaceActivitiesUrl,
	bindWorkplaceCompensationsUrl,
	getAllActivitiesUrl,
	getAllCompensationsUrl,
	getAllWorkplacesUrl,
	WORKING_CONDITIONS_CLASSES
} from '../../../helpers/constants';


const TABS = [
	{ id: 'activities', label: 'Вредные факторы' },
	{ id: 'compensations', label: 'Гарантии и компенсации' }
];

const classOptions = WORKING_CONDITIONS_CLASSES.map(value => ({ value, label: value }));

const EMPTY_ACTIVITY = { bound: false, working_conditions_class: null };
const EMPTY_COMPENSATION = {
	need_to_establish: false,
	actual_availability: false,
	reason: null
};

/** Links from the API -> keyed form state. */
const activityStateFromLinks = (links = []) => Object.fromEntries(
	links.map(link => [link.activity.id, {
		bound: true,
		working_conditions_class: link.working_conditions_class ?? null
	}])
);

const compensationStateFromLinks = (links = []) => Object.fromEntries(
	links.map(link => [link.compensation.id, {
		need_to_establish: link.need_to_establish,
		actual_availability: link.actual_availability,
		reason: link.reason ?? null
	}])
);

/** Factors are an open list: only the ones ticked on are sent. */
const normalizeActivities = (state) => Object.entries(state)
	.filter(([, row]) => row.bound)
	.map(([id, row]) => ({
		activity_id: Number(id),
		working_conditions_class: row.working_conditions_class ?? null
	}))
	.sort((a, b) => a.activity_id - b.activity_id);

/** A compensation row carries meaning only once a flag is set or a reason typed. */
const isFilledCompensation = (row) => !!row
	&& (row.need_to_establish || row.actual_availability || !!row.reason);

/**
 * Compensations are a fixed checklist (SOUT card, row 040) — every one is always
 * listed, so there is nothing to tick on. Rows left completely empty are simply
 * not stored.
 */
const normalizeCompensations = (state, options) => options
	.map(option => Number(option.value))
	.filter(id => isFilledCompensation(state[id]))
	.map(id => ({
		compensation_id: id,
		need_to_establish: !!state[id].need_to_establish,
		actual_availability: !!state[id].actual_availability,
		reason: state[id].reason ?? null
	}))
	.sort((a, b) => a.compensation_id - b.compensation_id);

export function BindWorkplace({ workplace, setRefreshKey }) {
	const api = useApi();
	const [activeTab, setActiveTab] = useState('activities');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [saveOk, setSaveOk] = useState(false);
	const [errorApi, setErrorApi] = useState(undefined);

	const [baseActivities, setBaseActivities] = useState({});
	const [baseCompensations, setBaseCompensations] = useState({});
	const [activityState, setActivityState] = useState({});
	const [compensationState, setCompensationState] = useState({});

	const {
		options: optionsActivities,
		itemDict: activitiesDict,
		error: errorActivities,
		loading: loadingActivities
	} = useFillSelect({ endpoint: getAllActivitiesUrl, labelField: 'title' });

	const {
		options: optionsCompensations,
		itemDict: compensationsDict,
		error: errorCompensations,
		loading: loadingCompensations
	} = useFillSelect({ endpoint: getAllCompensationsUrl, labelField: 'title' });

	const loadWorkplace = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get(`${getAllWorkplacesUrl}${workplace.id}`);
			const activities = activityStateFromLinks(data.activity_links);
			const compensations = compensationStateFromLinks(data.compensation_links);
			setBaseActivities(activities);
			setBaseCompensations(compensations);
			setActivityState(activities);
			setCompensationState(compensations);
		} catch (e) {
			setErrorApi(
				e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка'
			);
		} finally {
			setLoading(false);
		}
	}, [api, workplace.id]);

	useEffect(() => {
		loadWorkplace();
	}, [loadWorkplace]);

	useEffect(() => {
		setErrorApi(prev => prev ?? (errorActivities || errorCompensations));
	}, [errorActivities, errorCompensations]);

	const activitiesDirty = useMemo(
		() => JSON.stringify(normalizeActivities(activityState))
			!== JSON.stringify(normalizeActivities(baseActivities)),
		[activityState, baseActivities]
	);

	const compensationsDirty = useMemo(
		() => JSON.stringify(normalizeCompensations(compensationState, optionsCompensations))
			!== JSON.stringify(normalizeCompensations(baseCompensations, optionsCompensations)),
		[compensationState, baseCompensations, optionsCompensations]
	);

	const isDirty = activitiesDirty || compensationsDirty;

	const patchRow = (setState, emptyRow) => (id, patch) => setState(prev => ({
		...prev,
		[id]: { ...emptyRow, ...prev[id], ...patch }
	}));

	const patchActivity = patchRow(setActivityState, EMPTY_ACTIVITY);
	const patchCompensation = patchRow(setCompensationState, EMPTY_COMPENSATION);

	const buildRows = (options, dict, state) => options.map(option => {
		const id = Number(option.value);
		return {
			id,
			title: option.label,
			description: dict[option.value]?.description,
			row: state[id]
		};
	});

	const activityRows = useMemo(
		() => buildRows(optionsActivities, activitiesDict, activityState),
		[optionsActivities, activitiesDict, activityState]
	);

	const compensationRows = useMemo(
		() => buildRows(optionsCompensations, compensationsDict, compensationState),
		[optionsCompensations, compensationsDict, compensationState]
	);

	const activityColumns = [
		{
			title: '',
			key: 'bound',
			width: 48,
			render: (_, record) => (
				<Checkbox
					checked={!!record.row?.bound}
					onChange={(e) => patchActivity(record.id, { bound: e.target.checked })}
				/>
			)
		},
		{
			title: 'Вредный фактор',
			dataIndex: 'title',
			key: 'title',
			render: (title, record) => (
				<span className={record.row?.bound ? '' : styles.mutedCell}>{title}</span>
			)
		},
		{
			title: 'Класс условий',
			key: 'class',
			width: 160,
			render: (_, record) => (
				<Select
					className={styles.cellControl}
					size="small"
					allowClear
					disabled={!record.row?.bound}
					placeholder="—"
					value={record.row?.working_conditions_class ?? undefined}
					options={classOptions}
					// The modal overlay sits at z-index 9999; without this the
					// dropdown renders in <body> underneath it and looks dead.
					styles={{ popup: { root: { zIndex: 10000 } } }}
					onChange={(value) => patchActivity(record.id, {
						working_conditions_class: value ?? null
					})}
				/>
			)
		}
	];

	const compensationColumns = [
		{
			title: 'Гарантия / компенсация',
			dataIndex: 'title',
			key: 'title'
		},
		{
			title: 'Необходимость установления',
			key: 'need_to_establish',
			width: 200,
			align: 'center',
			render: (_, record) => (
				<Checkbox
					checked={!!record.row?.need_to_establish}
					onChange={(e) => patchCompensation(record.id, {
						need_to_establish: e.target.checked
					})}
				/>
			)
		},
		{
			title: 'Фактическое наличие',
			key: 'actual_availability',
			width: 170,
			align: 'center',
			render: (_, record) => (
				<Checkbox
					checked={!!record.row?.actual_availability}
					onChange={(e) => patchCompensation(record.id, {
						actual_availability: e.target.checked
					})}
				/>
			)
		},
		{
			title: 'Основание',
			key: 'reason',
			width: 220,
			render: (_, record) => (
				<Input
					className={styles.cellControl}
					size="small"
					placeholder="Основание"
					value={record.row?.reason ?? ''}
					onChange={(e) => patchCompensation(record.id, {
						reason: e.target.value || null
					})}
				/>
			)
		}
	];

	const saveChanges = async () => {
		setSaving(true);
		setErrorApi(undefined);
		try {
			if (activitiesDirty) {
				await api.put(
					bindWorkplaceActivitiesUrl(workplace.id),
					normalizeActivities(activityState)
				);
			}
			if (compensationsDirty) {
				await api.put(
					bindWorkplaceCompensationsUrl(workplace.id),
					normalizeCompensations(compensationState, optionsCompensations)
				);
			}
			await loadWorkplace();
			setRefreshKey?.((prev) => prev + 1);
			setSaveOk(true);
			setTimeout(() => setSaveOk(false), 1500);
		} catch (e) {
			setErrorApi(
				e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка'
			);
			await loadWorkplace();
		} finally {
			setSaving(false);
		}
	};

	const resetChanges = () => {
		setActivityState(baseActivities);
		setCompensationState(baseCompensations);
	};

	const isLoading = loading || loadingActivities || loadingCompensations;

	const helperText = (
		<p className={styles.helperText}>
			Рабочее место <b>{workplace.external_id}</b> — {workplace.position}.
			{activeTab === 'activities'
				? ' Отметьте вредные факторы из карты СОУТ и укажите класс условий.'
				: ' Перечень гарантий фиксирован — заполните по нему строку 040 карты СОУТ.'}
		</p>
	);

	const counterText = activeTab === 'activities'
		? `Отмечено факторов: ${normalizeActivities(activityState).length}`
		: 'Установлено гарантий: '
			+ normalizeCompensations(compensationState, optionsCompensations)
				.filter(row => row.need_to_establish || row.actual_availability).length;

	return (
		<div className={styles.container}>
			<h1 className={styles.title}>Привязки рабочего места</h1>
			<ErrorMessage error={errorApi} onClose={() => setErrorApi(undefined)} />
			<SurfaceBar
				className={styles.surfaceBar}
				actions={helperText}
				tabs={TABS}
				activeTab={activeTab}
				onTabChange={setActiveTab}
			/>
			{isLoading ? (
				<div className={styles.loadingWrap}><Spinner /></div>
			) : (
				<div className={styles.content}>
					{activeTab === 'activities' && (
						<Table
							size="small"
							rowKey="id"
							className={styles.table}
							columns={activityColumns}
							dataSource={activityRows}
							pagination={false}
							scroll={{ y: 380 }}
						/>
					)}
					{activeTab === 'compensations' && (
						<Table
							size="small"
							rowKey="id"
							className={styles.table}
							columns={compensationColumns}
							dataSource={compensationRows}
							pagination={false}
							scroll={{ y: 380 }}
						/>
					)}
					<div className={styles.footer}>
						<span className={styles.counter}>{counterText}</span>
						<div className={styles.actions}>
							<Button
								onClick={saveChanges}
								disabled={!isDirty || saving}
								className={`${styles.btn} ${saveOk ? styles.btnOk : ''}`}
							>
								{saveOk ? '✓ Сохранено' : 'Сохранить'}
							</Button>
							<Button
								onClick={resetChanges}
								disabled={!isDirty || saving}
								className={styles.btn}
							>
								Сбросить
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}